import {
  ChatComposerLayout,
  ChatNavigationLayout
} from "./chunk-T54JQSKI.js";
import {
  InlineSpinner_default
} from "./chunk-KAWLIR3H.js";

// src/index.ts
import { useEffect as useEffect4, useRef as useRef4, useState as useState5 } from "react";
import {
  deriveGenerationStatus,
  GENERATION_STATUS,
  STATUS_RANK
} from "@photon/chat-core";

// src/GenerationStatusIndicator.tsx
import ContentLoaderModule from "react-content-loader";
import { Square } from "lucide-react";

// src/ResponseLoader.tsx
import { jsx } from "react/jsx-runtime";
var ResponseLoader = ({ className, theme }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: `inline-block w-4 h-4 border-2 border-t-2 opacity-100 rounded-[50%] animate-spin ${theme === "dark" ? "border-edge-muted-control border-t-edge-white" : "border-edge-border border-t-edge-primary"} ${className ?? ""}`
  }
);
var ResponseLoader_default = ResponseLoader;

// src/GenerationStatusIndicator.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var ContentLoader = ContentLoaderModule.default ?? ContentLoaderModule;
var ActiveStopButton = ({ onStop }) => /* @__PURE__ */ jsx2(
  "button",
  {
    type: "button",
    onClick: onStop,
    "aria-label": "Stop generation",
    title: "Stop generation",
    className: "photon-primary-action flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-fill-primary text-ink-on-primary hover:bg-fill-primary-hover transition-colors",
    children: /* @__PURE__ */ jsx2(Square, { className: "h-3 w-3 fill-current stroke-none" })
  }
);
var GenerationStatusIndicator = ({
  visible,
  isStuck,
  hasContent,
  hasRunningTool,
  statusText = "Working",
  generateStatus,
  skeletonElapsed,
  thinkingElapsed,
  theme,
  canStop,
  onStop,
  onDismiss
}) => {
  if (!visible) return null;
  if (isStuck) {
    return /* @__PURE__ */ jsxs("div", { className: "py-2 flex items-center gap-3 text-sm text-ink-gray-500 dark:text-ink-gray-400", children: [
      /* @__PURE__ */ jsx2("span", { children: "Generation was interrupted." }),
      /* @__PURE__ */ jsx2(
        "button",
        {
          onClick: onDismiss,
          className: "text-xs px-2.5 py-1 rounded-md border border-edge-gray-300 dark:border-edge-gray-600 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-800 transition-colors",
          children: "Dismiss"
        }
      )
    ] });
  }
  if (!hasContent) {
    return /* @__PURE__ */ jsxs("div", { className: "py-2 w-full max-w-[520px]", children: [
      /* @__PURE__ */ jsxs(
        ContentLoader,
        {
          speed: 1.4,
          width: "100%",
          height: 72,
          backgroundColor: "var(--photon-chat-surface-muted)",
          foregroundColor: "var(--photon-chat-accent-soft)",
          children: [
            /* @__PURE__ */ jsx2("rect", { x: "0", y: "0", rx: "4", ry: "4", width: "88%", height: "12" }),
            /* @__PURE__ */ jsx2("rect", { x: "0", y: "20", rx: "4", ry: "4", width: "75%", height: "12" }),
            /* @__PURE__ */ jsx2("rect", { x: "0", y: "40", rx: "4", ry: "4", width: "60%", height: "12" }),
            /* @__PURE__ */ jsx2("rect", { x: "0", y: "60", rx: "4", ry: "4", width: "40%", height: "12" })
          ]
        }
      ),
      statusText || generateStatus === "stalled" ? /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center gap-2", children: [
        /* @__PURE__ */ jsxs("span", { className: `text-xs font-medium animate-pulse ${generateStatus === "stalled" ? "text-ink-amber-500 dark:text-ink-amber-400" : "text-ink-primary dark:text-ink-accent"}`, children: [
          generateStatus === "stalled" ? "Still working on it" : statusText,
          generateStatus !== "stalled" && skeletonElapsed >= 5 ? /* @__PURE__ */ jsxs("span", { className: "text-ink-gray-400 dark:text-ink-gray-500 ml-1", children: [
            skeletonElapsed,
            "s"
          ] }) : null,
          /* @__PURE__ */ jsxs("span", { className: "beaming-dots", children: [
            /* @__PURE__ */ jsx2("span", { className: "dot", children: "." }),
            /* @__PURE__ */ jsx2("span", { className: "dot", children: "." }),
            /* @__PURE__ */ jsx2("span", { className: "dot", children: "." })
          ] })
        ] }),
        canStop ? /* @__PURE__ */ jsx2(ActiveStopButton, { onStop }) : null
      ] }) : null
    ] });
  }
  if (hasRunningTool) {
    return canStop ? /* @__PURE__ */ jsx2("div", { className: "flex items-center py-1", children: /* @__PURE__ */ jsx2(ActiveStopButton, { onStop }) }) : null;
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx2(
      "div",
      {
        className: "relative rounded-full flex items-center justify-center",
        style: { height: "32px", width: "32px" },
        children: /* @__PURE__ */ jsx2("div", { className: "w-4 h-4 rounded-full absolute", role: "status", children: /* @__PURE__ */ jsx2(ResponseLoader_default, { theme }) })
      }
    ),
    /* @__PURE__ */ jsxs("span", { className: `text-xs italic font-medium ${generateStatus === "stalled" ? "text-ink-amber-500 dark:text-ink-amber-400" : "text-ink-primary dark:text-ink-accent"}`, children: [
      generateStatus === "stalled" ? "Still working on it" : statusText,
      generateStatus !== "stalled" && thinkingElapsed >= 2 ? /* @__PURE__ */ jsxs("span", { className: "text-ink-gray-400 dark:text-ink-gray-500 ml-1 not-italic", children: [
        thinkingElapsed,
        "s"
      ] }) : null,
      /* @__PURE__ */ jsxs("span", { className: "beaming-dots", children: [
        /* @__PURE__ */ jsx2("span", { className: "dot", children: "." }),
        /* @__PURE__ */ jsx2("span", { className: "dot", children: "." }),
        /* @__PURE__ */ jsx2("span", { className: "dot", children: "." })
      ] })
    ] }),
    canStop ? /* @__PURE__ */ jsx2(ActiveStopButton, { onStop }) : null
  ] });
};
var GenerationStatusIndicator_default = GenerationStatusIndicator;

// src/ChatNavigation.tsx
import { useMemo, useState } from "react";
import { ChevronDown, ExternalLink, Pin, Plus, Search } from "lucide-react";

// src/NavigationIcons.tsx
import { useId } from "react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function SparkIcon() {
  const gradientId = useId();
  return /* @__PURE__ */ jsxs2("svg", { className: "photon-navigation__brand-icon photon-navigation__brand-icon--spark", width: "20", height: "18", viewBox: "0 0 20 17.541", "aria-hidden": "true", children: [
    /* @__PURE__ */ jsx3("defs", { children: /* @__PURE__ */ jsxs2("linearGradient", { id: gradientId, y1: "0.26", x2: "1", y2: "0.5", children: [
      /* @__PURE__ */ jsx3("stop", { offset: "0", stopColor: "var(--photon-product-gradient-start)" }),
      /* @__PURE__ */ jsx3("stop", { offset: "1", stopColor: "var(--photon-product-secondary)" })
    ] }) }),
    /* @__PURE__ */ jsxs2("g", { transform: "translate(-22 -164)", children: [
      /* @__PURE__ */ jsxs2("g", { transform: "translate(-4.36 -3.8)", children: [
        /* @__PURE__ */ jsx3("path", { d: "M17.7,0H0V11.475l9.35.706,4.615,3.063V12.181l3.739-.706Z", transform: "translate(27.671 168.866)", fill: "var(--photon-product-soft)", opacity: "0.3" }),
        /* @__PURE__ */ jsx3("path", { d: "M19.037,8.375H5.465A3.211,3.211,0,0,0,2.25,11.551v7.656a3.211,3.211,0,0,0,3.213,3.178h7.245l4.085,3.367a.721.721,0,0,0,.76.1.706.706,0,0,0,.411-.64V22.385h1.072a3.211,3.211,0,0,0,3.215-3.176V11.553A3.211,3.211,0,0,0,19.037,8.375ZM5.465,20.973a1.787,1.787,0,0,1-1.786-1.768V11.553A1.787,1.787,0,0,1,5.466,9.787H19.034a1.787,1.787,0,0,1,1.788,1.768v7.652a1.787,1.787,0,0,1-1.788,1.766H17.25a.71.71,0,0,0-.714.706V23.7l-3.012-2.483a1.08,1.08,0,0,0-.687-.246Z", transform: "translate(24.11 159.425)", fill: `url(#${gradientId})` })
      ] }),
      /* @__PURE__ */ jsx3("path", { d: "M10.733,5.444H8.957l.413-2.073a.272.272,0,1,0-.533-.1L8.413,5.444H6.229l.413-2.073a.272.272,0,1,0-.533-.1L5.685,5.444H3.8a.268.268,0,0,0,0,.536H5.579L5.152,8.122H3.267a.268.268,0,0,0,0,.536H5.043l-.413,2.073a.268.268,0,0,0,.211.313.181.181,0,0,0,.053,0,.267.267,0,0,0,.267-.214l.427-2.172H7.771l-.413,2.073a.268.268,0,0,0,.211.313.165.165,0,0,0,.051,0,.267.267,0,0,0,.267-.214l.429-2.172H10.2a.268.268,0,0,0,0-.536H8.421L8.851,5.98h1.883a.268.268,0,0,0,0-.536ZM7.877,8.122H5.693L6.123,5.98H8.307Z", transform: "translate(25 163.954)", fill: "var(--photon-product-spark-detail)" })
    ] })
  ] });
}
function SpectraIcon() {
  return /* @__PURE__ */ jsx3("svg", { className: "photon-navigation__brand-icon photon-navigation__brand-icon--spectra", width: "24", height: "20", viewBox: "0 0 24 19.2", "aria-hidden": "true", children: /* @__PURE__ */ jsxs2("g", { transform: "translate(-568 -53)", children: [
    /* @__PURE__ */ jsx3("path", { d: "M12.96,0H0V8.4l6.845.517,3.378,2.242V8.917L12.96,8.4Z", transform: "translate(575.32 53.781)", fill: "var(--photon-product-muted)", opacity: "0.3" }),
    /* @__PURE__ */ jsx3("path", { d: "M14.538,8.375H4.6A2.351,2.351,0,0,0,2.25,10.7v5.6A2.351,2.351,0,0,0,4.6,18.631H9.9l2.99,2.465a.528.528,0,0,0,.557.071.517.517,0,0,0,.3-.468V18.631h.784a2.351,2.351,0,0,0,2.353-2.325V10.7A2.351,2.351,0,0,0,14.538,8.375ZM4.6,17.6A1.308,1.308,0,0,1,3.3,16.3V10.7A1.308,1.308,0,0,1,4.6,9.409h9.931A1.308,1.308,0,0,1,15.844,10.7v5.6A1.308,1.308,0,0,1,14.536,17.6H13.23a.52.52,0,0,0-.523.517v1.481L10.5,17.777a.791.791,0,0,0-.5-.18Z", transform: "translate(572.11 44.625)", fill: "var(--photon-product-muted)" }),
    /* @__PURE__ */ jsx3("path", { d: "M0,0H12.96V8.4l-6.845.517L2.737,11.159V8.917L0,8.4Z", transform: "translate(568.84 57.44)", fill: "var(--photon-product-primary)", opacity: "0.3" }),
    /* @__PURE__ */ jsx3("path", { d: "M12.96,0H0V8.4l6.845.517,3.378,2.242V8.917L12.96,8.4Z", transform: "translate(578.2 60.201)", fill: "var(--photon-product-secondary)", opacity: "0.3" }),
    /* @__PURE__ */ jsx3("path", { d: "M4.6,8.375h9.935A2.351,2.351,0,0,1,16.89,10.7v5.6a2.351,2.351,0,0,1-2.352,2.326h-5.3L6.245,21.1a.528.528,0,0,1-.557.071.517.517,0,0,1-.3-.468V18.631H4.6A2.351,2.351,0,0,1,2.25,16.306V10.7A2.351,2.351,0,0,1,4.6,8.375ZM14.537,17.6A1.308,1.308,0,0,0,15.844,16.3V10.7a1.308,1.308,0,0,0-1.309-1.292H4.6A1.308,1.308,0,0,0,3.3,10.7v5.6A1.308,1.308,0,0,0,4.6,17.6H5.91a.52.52,0,0,1,.523.517v1.481l2.205-1.817a.791.791,0,0,1,.5-.18Z", transform: "translate(565.75 48.225)", fill: "var(--photon-product-gradient-start)" }),
    /* @__PURE__ */ jsx3("path", { d: "M14.538,8.375H4.6A2.351,2.351,0,0,0,2.25,10.7v5.6A2.351,2.351,0,0,0,4.6,18.631H9.9l2.99,2.465a.528.528,0,0,0,.557.071.517.517,0,0,0,.3-.468V18.631h.784a2.351,2.351,0,0,0,2.353-2.325V10.7A2.351,2.351,0,0,0,14.538,8.375ZM4.6,17.6A1.308,1.308,0,0,1,3.3,16.3V10.7A1.308,1.308,0,0,1,4.6,9.409h9.931A1.308,1.308,0,0,1,15.844,10.7v5.6A1.308,1.308,0,0,1,14.536,17.6H13.23a.52.52,0,0,0-.523.517v1.481L10.5,17.777a.791.791,0,0,0-.5-.18Z", transform: "translate(575.11 50.985)", fill: "var(--photon-product-secondary)" })
  ] }) });
}

// src/ChatNavigation.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var sortPinned = (items) => items.map((item, index) => ({ item, index })).sort((left, right) => {
  const pinnedDifference = Number(Boolean(right.item.pinned)) - Number(Boolean(left.item.pinned));
  if (pinnedDifference) return pinnedDifference;
  if (left.item.pinned && right.item.pinned) {
    const dateDifference = (right.item.pinned_date || right.item.pinned_at || "").localeCompare(left.item.pinned_date || left.item.pinned_at || "");
    if (dateDifference) return dateDifference;
  }
  return left.index - right.index;
}).map(({ item }) => item);
function ChatNavigation({
  conversations,
  collections,
  collectionConversations,
  selectedCollectionId,
  collectionsUrl,
  isLoading = false,
  isLoadingCollections,
  isLoadingConversations,
  onOpenCollection,
  onOpenConversation,
  onStartConversation,
  onPinCollection,
  onPinConversation
}) {
  const collectionsLoading = isLoadingCollections ?? isLoading;
  const conversationsLoading = isLoadingConversations ?? isLoading;
  const [query, setQuery] = useState("");
  const [collectionsOpen, setCollectionsOpen] = useState(true);
  const [conversationsOpen, setConversationsOpen] = useState(true);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleConversations = useMemo(() => sortPinned(normalizedQuery ? conversations.filter((item) => (item.title || "Untitled Spark").toLowerCase().includes(normalizedQuery)) : conversations), [conversations, normalizedQuery]);
  const visibleCollectionConversations = useMemo(() => sortPinned(normalizedQuery ? collectionConversations.filter((item) => (item.title || "Untitled Spark").toLowerCase().includes(normalizedQuery)) : collectionConversations), [collectionConversations, normalizedQuery]);
  const visibleCollections = useMemo(() => sortPinned(normalizedQuery ? collections.filter((collection) => (collection.name || collection.title || "Untitled Spectra").toLowerCase().includes(normalizedQuery)) : collections), [collections, normalizedQuery]);
  return /* @__PURE__ */ jsxs3(ChatNavigationLayout, { variant: "panel", children: [
    /* @__PURE__ */ jsx4("div", { className: "photon-navigation__heading", children: /* @__PURE__ */ jsxs3("div", { children: [
      /* @__PURE__ */ jsx4("strong", { children: "Photon" }),
      /* @__PURE__ */ jsx4("span", { children: "Your recent work" })
    ] }) }),
    /* @__PURE__ */ jsxs3("label", { className: "photon-navigation__search", children: [
      /* @__PURE__ */ jsx4(Search, { size: 15, "aria-hidden": "true" }),
      /* @__PURE__ */ jsx4("input", { value: query, onChange: (event) => setQuery(event.target.value), placeholder: "Search Sparks and Spectras" })
    ] }),
    /* @__PURE__ */ jsxs3("div", { className: `photon-navigation__section ${collectionsOpen || query ? "open" : ""}`, children: [
      /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__section-header", children: [
        /* @__PURE__ */ jsx4("button", { type: "button", className: "photon-navigation__icon-button", onClick: () => setCollectionsOpen((open) => !open), "aria-label": collectionsOpen ? "Collapse Spectra" : "Expand Spectra", children: /* @__PURE__ */ jsx4(ChevronDown, { size: 15, className: collectionsOpen ? "" : "collapsed" }) }),
        /* @__PURE__ */ jsx4(SpectraIcon, {}),
        /* @__PURE__ */ jsx4("strong", { children: "Spectra" }),
        collectionsUrl && /* @__PURE__ */ jsxs3("a", { href: collectionsUrl, target: "_blank", rel: "noreferrer", children: [
          "Manage ",
          /* @__PURE__ */ jsx4(ExternalLink, { size: 12 })
        ] })
      ] }),
      (collectionsOpen || query) && /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__list", children: [
        collectionsLoading && /* @__PURE__ */ jsx4("p", { className: "photon-navigation__empty", children: "Loading Spectra..." }),
        !collectionsLoading && visibleCollections.length === 0 && /* @__PURE__ */ jsx4("p", { className: "photon-navigation__empty", children: "No Spectra found." }),
        visibleCollections.map((collection) => {
          const expanded = selectedCollectionId === collection.id;
          const name = collection.name || collection.title || "Untitled Spectra";
          return /* @__PURE__ */ jsxs3("div", { children: [
            /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__collection-row", children: [
              /* @__PURE__ */ jsxs3("button", { type: "button", className: "photon-navigation__collection", onClick: () => onOpenCollection(collection), "aria-expanded": expanded, "aria-label": `${expanded ? "Collapse" : "Expand"} ${name}`, children: [
                /* @__PURE__ */ jsx4(ChevronDown, { size: 14, className: expanded ? "" : "collapsed" }),
                /* @__PURE__ */ jsx4("span", { children: name })
              ] }),
              onPinCollection && /* @__PURE__ */ jsx4("button", { type: "button", className: `photon-navigation__icon-button photon-navigation__pin${collection.pinned ? " pinned" : ""}`, onClick: () => onPinCollection(collection, !collection.pinned), "aria-pressed": Boolean(collection.pinned), "aria-label": `${collection.pinned ? "Unpin" : "Pin"} ${name}`, title: collection.pinned ? "Unpin" : "Pin to top", children: /* @__PURE__ */ jsx4(Pin, { size: 13, fill: collection.pinned ? "currentColor" : "none" }) }),
              /* @__PURE__ */ jsx4("button", { type: "button", className: "photon-navigation__icon-button", onClick: () => onStartConversation(collection), "aria-label": `New Spark in ${name}`, title: "New Spark", children: /* @__PURE__ */ jsx4(Plus, { size: 14 }) })
            ] }),
            expanded && /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__nested-list", children: [
              isLoading && /* @__PURE__ */ jsx4("p", { className: "photon-navigation__empty", children: "Loading Sparks..." }),
              !isLoading && visibleCollectionConversations.length === 0 && /* @__PURE__ */ jsx4("p", { className: "photon-navigation__empty", children: "Add or move Spark here..." }),
              visibleCollectionConversations.map((conversation) => {
                const title = conversation.title || "Untitled Spark";
                return /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__item-row", children: [
                  /* @__PURE__ */ jsx4("button", { type: "button", className: "photon-navigation__item", onClick: () => onOpenConversation(conversation, collection), children: /* @__PURE__ */ jsx4("span", { children: title }) }),
                  onPinConversation && /* @__PURE__ */ jsx4("button", { type: "button", className: `photon-navigation__icon-button photon-navigation__pin${conversation.pinned ? " pinned" : ""}`, onClick: () => onPinConversation(conversation, !conversation.pinned, collection), "aria-pressed": Boolean(conversation.pinned), "aria-label": `${conversation.pinned ? "Unpin" : "Pin"} ${title}`, title: conversation.pinned ? "Unpin" : "Pin to top", children: /* @__PURE__ */ jsx4(Pin, { size: 13, fill: conversation.pinned ? "currentColor" : "none" }) })
                ] }, conversation.uid);
              })
            ] })
          ] }, collection.id);
        })
      ] })
    ] }),
    /* @__PURE__ */ jsxs3("div", { className: `photon-navigation__section ${conversationsOpen || query ? "open" : ""}`, children: [
      /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__section-header", children: [
        /* @__PURE__ */ jsx4("button", { type: "button", className: "photon-navigation__icon-button", onClick: () => setConversationsOpen((open) => !open), "aria-label": conversationsOpen ? "Collapse Spark" : "Expand Spark", children: /* @__PURE__ */ jsx4(ChevronDown, { size: 15, className: conversationsOpen ? "" : "collapsed" }) }),
        /* @__PURE__ */ jsx4(SparkIcon, {}),
        /* @__PURE__ */ jsx4("strong", { children: "Spark" }),
        /* @__PURE__ */ jsx4("button", { type: "button", className: "photon-navigation__icon-button", onClick: () => onStartConversation(), "aria-label": "New personal Spark", title: "New Spark", children: /* @__PURE__ */ jsx4(Plus, { size: 15 }) })
      ] }),
      (conversationsOpen || query) && /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__list", children: [
        conversationsLoading && /* @__PURE__ */ jsx4("p", { className: "photon-navigation__empty", children: "Loading Sparks..." }),
        !conversationsLoading && visibleConversations.length === 0 && /* @__PURE__ */ jsx4("p", { className: "photon-navigation__empty", children: "No personal Sparks found." }),
        visibleConversations.map((conversation) => {
          const title = conversation.title || "Untitled Spark";
          return /* @__PURE__ */ jsxs3("div", { className: "photon-navigation__item-row", children: [
            /* @__PURE__ */ jsx4("button", { type: "button", className: "photon-navigation__item", onClick: () => onOpenConversation(conversation), children: /* @__PURE__ */ jsx4("span", { children: title }) }),
            onPinConversation && /* @__PURE__ */ jsx4("button", { type: "button", className: `photon-navigation__icon-button photon-navigation__pin${conversation.pinned ? " pinned" : ""}`, onClick: () => onPinConversation(conversation, !conversation.pinned), "aria-pressed": Boolean(conversation.pinned), "aria-label": `${conversation.pinned ? "Unpin" : "Pin"} ${title}`, title: conversation.pinned ? "Unpin" : "Pin to top", children: /* @__PURE__ */ jsx4(Pin, { size: 13, fill: conversation.pinned ? "currentColor" : "none" }) })
          ] }, conversation.uid);
        })
      ] })
    ] })
  ] });
}

// src/ComposerControls.tsx
import { useEffect, useRef, useState as useState2 } from "react";
import { Check, Globe, Mic, Monitor, Paperclip, SlidersHorizontal, WandSparkles, X } from "lucide-react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function ComposerControls({
  models,
  selectedModelId,
  onModelChange,
  styles,
  activeStyleId,
  styleSelection,
  onStyleChange,
  skills,
  selectedSkillIds,
  onSkillToggle,
  devices,
  selectedDeviceId,
  onDeviceChange,
  files,
  onFilesSelected,
  onFileRemove,
  onTranscript,
  action,
  webSearchEnabled = true,
  onWebSearchToggle,
  webSearchDisabled = false,
  disabled = false,
  isUploading = false,
  compact = false,
  showVoice = true
}) {
  const fileInputRef = useRef(null);
  const skillsMenuRef = useRef(null);
  const modelMenuRef = useRef(null);
  const styleMenuRef = useRef(null);
  const deviceMenuRef = useRef(null);
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState2(false);
  const [voiceError, setVoiceError] = useState2("");
  useEffect(() => () => recognitionRef.current?.abort(), []);
  useEffect(() => {
    const dismissMenus = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      const menus = [skillsMenuRef.current, styleMenuRef.current, modelMenuRef.current, deviceMenuRef.current];
      if (menus.some((menu) => menu?.contains(target))) return;
      menus.forEach((menu) => menu?.removeAttribute("open"));
    };
    document.addEventListener("pointerdown", dismissMenus);
    return () => document.removeEventListener("pointerdown", dismissMenus);
  }, []);
  const toggleVoice = async () => {
    setVoiceError("");
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    const recognitionConstructor = window.webkitSpeechRecognition;
    if (!recognitionConstructor) {
      setVoiceError("Voice input is not supported by this Chrome version.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      const recognition = new recognitionConstructor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";
      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => {
        setIsListening(false);
        setVoiceError("Voice input stopped. Check microphone access and try again.");
      };
      recognition.onresult = (event) => {
        let transcript = "";
        for (let index = 0; index < event.results.length; index += 1) {
          if (event.results[index].isFinal) transcript += event.results[index][0].transcript;
        }
        if (transcript) onTranscript(transcript);
      };
      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      setVoiceError("Microphone access is required for voice input.");
    }
  };
  const selectedStyleKey = styleSelection.mode === "style" ? styleSelection.style_id : styleSelection.mode;
  const styleOptions = [
    ...activeStyleId ? [{ key: "profile_default", name: styles.find((style) => style.style_id === activeStyleId)?.name || "Profile style", selection: { mode: "profile_default" } }] : [],
    ...styles.filter((style) => style.style_id !== activeStyleId).map((style) => ({
      key: style.style_id,
      name: style.name,
      selection: { mode: "style", style_id: style.style_id }
    })),
    { key: "none", name: "Photon default", selection: { mode: "none" } }
  ];
  const selectedModel = models.find((model) => model.id === selectedModelId);
  return /* @__PURE__ */ jsxs4(ChatComposerLayout, { variant: "panel", className: compact ? "photon-composer--compact" : "", children: [
    files.length > 0 && /* @__PURE__ */ jsx5("div", { className: "photon-composer__attachments", "aria-label": "Selected attachments", children: files.map((entry, index) => /* @__PURE__ */ jsxs4("span", { className: "photon-composer__attachment", children: [
      /* @__PURE__ */ jsx5("span", { children: entry.file.name }),
      entry.assetId && /* @__PURE__ */ jsx5(Check, { size: 11, "aria-label": "Uploaded" }),
      /* @__PURE__ */ jsx5("button", { type: "button", onClick: () => onFileRemove(index), disabled, "aria-label": `Remove ${entry.file.name}`, title: "Remove attachment", children: /* @__PURE__ */ jsx5(X, { size: 12 }) })
    ] }, `${entry.file.name}-${entry.file.lastModified}`)) }),
    /* @__PURE__ */ jsxs4("div", { className: "photon-composer__toolbar", children: [
      /* @__PURE__ */ jsxs4("div", { className: "photon-composer__controls photon-composer__controls--primary", children: [
        /* @__PURE__ */ jsx5("button", { type: "button", className: "photon-composer__tool", onClick: () => fileInputRef.current?.click(), disabled: disabled || isUploading, "aria-label": "Attach files", title: "Attach files", children: /* @__PURE__ */ jsx5(Paperclip, { size: 15 }) }),
        /* @__PURE__ */ jsx5(
          "input",
          {
            ref: fileInputRef,
            className: "photon-visually-hidden",
            type: "file",
            multiple: true,
            onChange: (event) => {
              onFilesSelected(Array.from(event.target.files ?? []));
              event.target.value = "";
            },
            disabled
          }
        ),
        skills.length > 0 && /* @__PURE__ */ jsxs4("details", { className: "photon-composer__menu", name: "composer-menu", ref: skillsMenuRef, children: [
          /* @__PURE__ */ jsxs4("summary", { "aria-label": "Choose skills", title: "Skills", children: [
            /* @__PURE__ */ jsx5(WandSparkles, { size: 14 }),
            selectedSkillIds.length > 0 && /* @__PURE__ */ jsx5("span", { children: selectedSkillIds.length })
          ] }),
          /* @__PURE__ */ jsxs4("div", { className: "photon-composer__menu-panel", children: [
            /* @__PURE__ */ jsx5("strong", { children: "Skills" }),
            skills.map((skill) => /* @__PURE__ */ jsx5(
              "button",
              {
                type: "button",
                className: `photon-composer__option ${selectedSkillIds.includes(skill.skill_id) ? "selected" : ""}`,
                "aria-pressed": selectedSkillIds.includes(skill.skill_id),
                onClick: () => onSkillToggle(skill.skill_id),
                disabled,
                children: /* @__PURE__ */ jsx5("span", { children: skill.display_name })
              },
              skill.skill_id
            ))
          ] })
        ] }),
        /* @__PURE__ */ jsxs4("details", { className: "photon-composer__menu photon-composer__style-menu", name: "composer-menu", ref: styleMenuRef, children: [
          /* @__PURE__ */ jsx5("summary", { "aria-label": "Choose response style", title: "My Style", children: /* @__PURE__ */ jsx5("span", { className: "photon-composer__integral", "aria-hidden": "true", children: "\u222B" }) }),
          /* @__PURE__ */ jsxs4("div", { className: "photon-composer__menu-panel", children: [
            /* @__PURE__ */ jsx5("strong", { children: "My Style" }),
            styleOptions.map((option) => /* @__PURE__ */ jsx5(
              "button",
              {
                type: "button",
                className: `photon-composer__style-option ${selectedStyleKey === option.key ? "selected" : ""}`,
                "aria-pressed": selectedStyleKey === option.key,
                onClick: () => {
                  onStyleChange(option.selection);
                  styleMenuRef.current?.removeAttribute("open");
                },
                disabled,
                children: /* @__PURE__ */ jsx5("span", { children: option.name })
              },
              option.key
            ))
          ] })
        ] }),
        /* @__PURE__ */ jsxs4("details", { className: "photon-composer__menu photon-composer__model-menu", name: "composer-menu", ref: modelMenuRef, children: [
          /* @__PURE__ */ jsxs4("summary", { "aria-label": "Choose model", title: "Model", children: [
            /* @__PURE__ */ jsx5(SlidersHorizontal, { size: 13, "aria-hidden": "true" }),
            /* @__PURE__ */ jsx5("span", { className: "photon-composer__model-label", children: selectedModel?.name || "Model" })
          ] }),
          /* @__PURE__ */ jsxs4("div", { className: "photon-composer__menu-panel", children: [
            /* @__PURE__ */ jsx5("strong", { children: "Model" }),
            models.map((model) => /* @__PURE__ */ jsx5(
              "button",
              {
                type: "button",
                className: `photon-composer__option ${selectedModelId === model.id ? "selected" : ""}`,
                "aria-pressed": selectedModelId === model.id,
                onClick: () => {
                  onModelChange(model.id);
                  modelMenuRef.current?.removeAttribute("open");
                },
                disabled,
                children: /* @__PURE__ */ jsx5("span", { children: model.name })
              },
              model.id
            ))
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs4("div", { className: "photon-composer__controls photon-composer__controls--actions", children: [
        devices.length > 0 && /* @__PURE__ */ jsxs4("details", { className: "photon-composer__menu photon-composer__device-menu", name: "composer-menu", ref: deviceMenuRef, children: [
          /* @__PURE__ */ jsx5("summary", { "aria-label": "Choose Desktop Bridge device", title: "Desktop Bridge device", children: /* @__PURE__ */ jsx5(Monitor, { size: 14, "aria-hidden": "true" }) }),
          /* @__PURE__ */ jsxs4("div", { className: "photon-composer__menu-panel", children: [
            /* @__PURE__ */ jsx5("strong", { children: "Desktop Bridge" }),
            devices.length > 1 && /* @__PURE__ */ jsx5(
              "button",
              {
                type: "button",
                className: `photon-composer__option ${selectedDeviceId === "" ? "selected" : ""}`,
                "aria-pressed": selectedDeviceId === "",
                onClick: () => {
                  onDeviceChange("");
                  deviceMenuRef.current?.removeAttribute("open");
                },
                disabled,
                children: "No device"
              }
            ),
            devices.map((device) => /* @__PURE__ */ jsx5(
              "button",
              {
                type: "button",
                className: `photon-composer__option ${selectedDeviceId === device.device_id ? "selected" : ""}`,
                "aria-pressed": selectedDeviceId === device.device_id,
                onClick: () => {
                  onDeviceChange(device.device_id);
                  deviceMenuRef.current?.removeAttribute("open");
                },
                disabled,
                children: device.device_name || device.device_id
              },
              device.device_id
            ))
          ] })
        ] }),
        showVoice && /* @__PURE__ */ jsx5("button", { type: "button", className: `photon-composer__tool ${isListening ? "active" : ""}`, onClick: () => void toggleVoice(), disabled, "aria-label": isListening ? "Stop voice input" : "Start voice input", title: isListening ? "Stop voice input" : "Voice input", children: /* @__PURE__ */ jsx5(Mic, { size: 15 }) }),
        /* @__PURE__ */ jsx5(
          "button",
          {
            type: "button",
            className: `photon-composer__tool photon-composer__web-search${webSearchEnabled && !webSearchDisabled ? " enabled" : ""}`,
            onClick: () => onWebSearchToggle?.(),
            disabled: disabled || webSearchDisabled,
            "aria-pressed": Boolean(webSearchEnabled && !webSearchDisabled),
            "aria-label": webSearchDisabled ? "Web search disabled for this Spectra" : webSearchEnabled ? "Web search enabled" : "Web search disabled",
            title: webSearchDisabled ? "Web search is disabled for this Spectra" : webSearchEnabled ? "Web search enabled \u2014 click to disable" : "Web search disabled \u2014 click to enable",
            children: /* @__PURE__ */ jsx5(Globe, { size: 15 })
          }
        ),
        action
      ] })
    ] }),
    voiceError && /* @__PURE__ */ jsx5("span", { className: "photon-composer__error", role: "alert", children: voiceError })
  ] });
}

// src/EditablePrompt.tsx
import { useEffect as useEffect2, useRef as useRef2, useState as useState3 } from "react";
import { Check as Check2, X as X2 } from "lucide-react";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function EditablePrompt({
  value,
  children,
  disabled = false,
  onSubmit,
  onEditStart,
  onCancel,
  hasExternalChanges = false,
  toolbar
}) {
  const [isEditing, setIsEditing] = useState3(false);
  const [draft, setDraft] = useState3(value);
  const containerRef = useRef2(null);
  const textareaRef = useRef2(null);
  useEffect2(() => {
    if (!isEditing) setDraft(value);
  }, [isEditing, value]);
  useEffect2(() => {
    if (!isEditing) return;
    textareaRef.current?.focus();
    const cancelOnOutsideClick = (event) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setDraft(value);
        setIsEditing(false);
        onCancel?.();
      }
    };
    document.addEventListener("pointerdown", cancelOnOutsideClick);
    return () => document.removeEventListener("pointerdown", cancelOnOutsideClick);
  }, [isEditing, onCancel, value]);
  const cancel = () => {
    setDraft(value);
    setIsEditing(false);
    onCancel?.();
  };
  const submit = () => {
    const nextValue = draft.trim();
    if (!nextValue || nextValue === value.trim() && !hasExternalChanges) {
      cancel();
      return;
    }
    setIsEditing(false);
    onSubmit(nextValue);
  };
  const beginEditing = () => {
    setIsEditing(true);
    onEditStart?.();
  };
  const canSubmit = !!draft.trim() && (draft.trim() !== value.trim() || hasExternalChanges);
  if (!isEditing) {
    return /* @__PURE__ */ jsx6(
      "div",
      {
        className: `photon-editable-prompt${disabled ? " disabled" : ""}`,
        onClick: (event) => {
          if (!disabled && !event.target.closest("button, a, input, textarea, select")) {
            beginEditing();
          }
        },
        onKeyDown: (event) => {
          if (!disabled && event.key === "Enter" && event.target === event.currentTarget) {
            event.preventDefault();
            beginEditing();
          }
        },
        role: "group",
        "aria-label": "Prompt. Press Enter to edit.",
        tabIndex: disabled ? -1 : 0,
        children: children ?? value
      }
    );
  }
  return /* @__PURE__ */ jsxs5("div", { className: "photon-editable-prompt__editor", ref: containerRef, children: [
    /* @__PURE__ */ jsx6(
      "textarea",
      {
        ref: textareaRef,
        value: draft,
        onChange: (event) => setDraft(event.target.value),
        onKeyDown: (event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
          } else if (event.key === "Escape") {
            cancel();
          }
        },
        "aria-label": "Edit prompt",
        rows: Math.min(8, Math.max(2, draft.split("\n").length))
      }
    ),
    toolbar ? toolbar({ cancel, submit, canSubmit }) : /* @__PURE__ */ jsxs5("div", { className: "photon-editable-prompt__actions", children: [
      /* @__PURE__ */ jsx6("button", { type: "button", onClick: cancel, "aria-label": "Cancel prompt edit", title: "Cancel", children: /* @__PURE__ */ jsx6(X2, { size: 13 }) }),
      /* @__PURE__ */ jsx6("button", { type: "button", className: "primary", onClick: submit, "aria-label": "Save prompt edit", title: "Save", disabled: !canSubmit, children: /* @__PURE__ */ jsx6(Check2, { size: 13 }) })
    ] })
  ] });
}

// src/MessageFooter.tsx
import { useEffect as useEffect3, useRef as useRef3, useState as useState4 } from "react";
import { CalendarClock, Check as Check3, ChevronLeft, ChevronRight, Copy, Download, Leaf, RotateCcw, Trash2 } from "lucide-react";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function formatTokens(value) {
  return value >= 1e3 ? `${(value / 1e3).toFixed(1)}k` : String(value);
}
function usageTitle(message) {
  const usage = message.usage;
  if (!usage) return message.model_name || message.model_id;
  return [
    `Prompt: ${formatTokens(usage.tokens_input)}`,
    `Completion: ${formatTokens(usage.tokens_output)}`,
    ...usage.tokens_cached ? [`Cached: ${formatTokens(usage.tokens_cached)}`] : [],
    ...usage.cost_usd != null ? [`Cost: $${usage.cost_usd.toFixed(4)}`] : []
  ].join("\n");
}
var MessageFooter = (message) => {
  const [copied, setCopied] = useState4(false);
  const [showExportMenu, setShowExportMenu] = useState4(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState4(false);
  const [showRegenerateMenu, setShowRegenerateMenu] = useState4(false);
  const deleteMenuRef = useRef3(null);
  const regenerateMenuRef = useRef3(null);
  const modelName = message.model_name || message.model_id;
  const scheduledJobId = message.triggered_by?.startsWith("scheduler:") ? message.triggered_by.slice("scheduler:".length) : null;
  const hasRegenerateOptions = Boolean(message.regenerateModels?.length || message.regenerateStyles?.length);
  const activeStyle = message.regenerateStyles?.find((style) => style.style_id === message.activeStyleId);
  const regenerateStyles = [
    ...activeStyle ? [{ key: "profile_default", name: activeStyle.name, selection: { mode: "profile_default" } }] : [],
    ...(message.regenerateStyles ?? []).filter((style) => style.style_id !== message.activeStyleId).map((style) => ({ key: style.style_id, name: style.name, selection: { mode: "style", style_id: style.style_id } })),
    { key: "none", name: "Photon default", selection: { mode: "none" } }
  ];
  useEffect3(() => {
    if (!showRegenerateMenu && !showDeleteConfirm) return void 0;
    const dismissMenu = (event) => {
      if (!(event.target instanceof Node)) return;
      if (showRegenerateMenu && !regenerateMenuRef.current?.contains(event.target)) {
        setShowRegenerateMenu(false);
      }
      if (showDeleteConfirm && !deleteMenuRef.current?.contains(event.target)) {
        setShowDeleteConfirm(false);
      }
    };
    document.addEventListener("pointerdown", dismissMenu);
    return () => document.removeEventListener("pointerdown", dismissMenu);
  }, [showDeleteConfirm, showRegenerateMenu]);
  const copyMessage = async () => {
    if (!message.content) return;
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return /* @__PURE__ */ jsxs6("footer", { className: "photon-message-footer", children: [
    /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__actions", children: [
      /* @__PURE__ */ jsx7(
        "button",
        {
          type: "button",
          className: "photon-message-footer__action",
          onClick: () => void copyMessage(),
          "aria-label": copied ? "Message copied" : "Copy message",
          title: copied ? "Copied" : "Copy message",
          children: copied ? /* @__PURE__ */ jsx7(Check3, { size: 14 }) : /* @__PURE__ */ jsx7(Copy, { size: 14 })
        }
      ),
      message.onExport ? /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__export", children: [
        /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            className: "photon-message-footer__action",
            onClick: () => setShowExportMenu((visible) => !visible),
            "aria-label": "Export message",
            "aria-haspopup": "menu",
            "aria-expanded": showExportMenu,
            title: "Export message",
            children: /* @__PURE__ */ jsx7(Download, { size: 14 })
          }
        ),
        showExportMenu ? /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__export-menu", role: "menu", children: [
          /* @__PURE__ */ jsx7("button", { type: "button", role: "menuitem", onClick: () => {
            setShowExportMenu(false);
            void message.onExport?.("pdf");
          }, children: "Export as PDF" }),
          /* @__PURE__ */ jsx7("button", { type: "button", role: "menuitem", onClick: () => {
            setShowExportMenu(false);
            void message.onExport?.("docx");
          }, children: "Export as DOCX" })
        ] }) : null
      ] }) : null,
      message.onDelete ? /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__delete", ref: deleteMenuRef, children: [
        /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            className: "photon-message-footer__action",
            onClick: () => {
              if (message.isLatest !== false) {
                void message.onDelete?.(false);
                return;
              }
              setShowDeleteConfirm(true);
              setShowExportMenu(false);
              setShowRegenerateMenu(false);
            },
            "aria-label": "Delete message",
            "aria-haspopup": message.isLatest === false ? "dialog" : void 0,
            "aria-expanded": message.isLatest === false ? showDeleteConfirm : void 0,
            title: "Delete message",
            children: /* @__PURE__ */ jsx7(Trash2, { size: 14 })
          }
        ),
        showDeleteConfirm ? /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__delete-confirm", role: "alertdialog", "aria-labelledby": "photon-delete-title", children: [
          /* @__PURE__ */ jsx7("strong", { id: "photon-delete-title", children: "Delete this turn and all responses after it?" }),
          /* @__PURE__ */ jsx7("p", { children: "Later turns depend on this conversation context and will also be deleted. This cannot be undone." }),
          /* @__PURE__ */ jsxs6("div", { children: [
            /* @__PURE__ */ jsx7("button", { type: "button", onClick: () => setShowDeleteConfirm(false), children: "Cancel" }),
            /* @__PURE__ */ jsx7(
              "button",
              {
                type: "button",
                className: "danger",
                onClick: () => {
                  setShowDeleteConfirm(false);
                  void message.onDelete?.(true);
                },
                children: "Delete all"
              }
            )
          ] })
        ] }) : null
      ] }) : null,
      message.onRegenerate ? /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__regenerate", ref: regenerateMenuRef, children: [
        /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            className: "photon-message-footer__action",
            onClick: () => hasRegenerateOptions ? setShowRegenerateMenu((visible) => !visible) : void message.onRegenerate?.(),
            "aria-label": "Regenerate response",
            "aria-haspopup": hasRegenerateOptions ? "menu" : void 0,
            "aria-expanded": hasRegenerateOptions ? showRegenerateMenu : void 0,
            title: "Regenerate response",
            children: /* @__PURE__ */ jsx7(RotateCcw, { size: 14 })
          }
        ),
        showRegenerateMenu ? /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__regenerate-menu", role: "menu", children: [
          /* @__PURE__ */ jsx7("strong", { children: "Rephrase this answer" }),
          /* @__PURE__ */ jsx7("div", { className: "photon-message-footer__regenerate-options", children: message.regenerateModels?.map((model) => /* @__PURE__ */ jsxs6(
            "button",
            {
              type: "button",
              role: "menuitem",
              onClick: () => {
                setShowRegenerateMenu(false);
                void message.onRegenerate?.({
                  modelId: model.id,
                  styleSelection: message.regenerateStyleSelection,
                  styleVersionId: message.regenerateStyleVersionId
                });
              },
              children: [
                /* @__PURE__ */ jsxs6("span", { children: [
                  /* @__PURE__ */ jsx7("b", { children: model.name }),
                  model.description ? /* @__PURE__ */ jsx7("small", { children: model.description }) : null
                ] }),
                model.id === message.model_id ? /* @__PURE__ */ jsx7(Check3, { size: 13 }) : null
              ]
            },
            model.id
          )) }),
          /* @__PURE__ */ jsx7("span", { className: "photon-message-footer__regenerate-label", children: "Style" }),
          regenerateStyles.map((style) => /* @__PURE__ */ jsx7(
            "button",
            {
              type: "button",
              role: "menuitem",
              onClick: () => {
                setShowRegenerateMenu(false);
                void message.onRegenerate?.({
                  modelId: message.model_id,
                  styleSelection: style.selection
                });
              },
              children: style.name
            },
            style.key
          ))
        ] }) : null
      ] }) : null
    ] }),
    /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__metadata", children: [
      (message.answerCount ?? 0) > 1 ? /* @__PURE__ */ jsxs6("div", { className: "photon-message-footer__pagination", "aria-label": "Response versions", children: [
        /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            className: "photon-message-footer__action",
            onClick: message.onPreviousAnswer,
            disabled: (message.answerIndex ?? 0) === 0,
            "aria-label": "Previous response",
            title: "Previous response",
            children: /* @__PURE__ */ jsx7(ChevronLeft, { size: 14 })
          }
        ),
        /* @__PURE__ */ jsxs6("span", { children: [
          (message.answerIndex ?? 0) + 1,
          " / ",
          message.answerCount
        ] }),
        /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            className: "photon-message-footer__action",
            onClick: message.onNextAnswer,
            disabled: (message.answerIndex ?? 0) >= (message.answerCount ?? 1) - 1,
            "aria-label": "Next response",
            title: "Next response",
            children: /* @__PURE__ */ jsx7(ChevronRight, { size: 14 })
          }
        )
      ] }) : null,
      message.triggered_by && /* @__PURE__ */ jsxs6(
        "span",
        {
          className: "photon-message-footer__scheduled",
          title: scheduledJobId ? `Scheduled \xB7 ${scheduledJobId}` : "Generated by a scheduled job",
          children: [
            /* @__PURE__ */ jsx7(CalendarClock, { size: 12 }),
            "Scheduled"
          ]
        }
      ),
      (message.usage || modelName) && /* @__PURE__ */ jsxs6("span", { className: "photon-message-footer__model", title: usageTitle(message), children: [
        message.usage?.tokens_total ? /* @__PURE__ */ jsx7("span", { className: "photon-message-footer__token-total", children: formatTokens(message.usage.tokens_total) }) : null,
        message.usage?.tokens_cached ? /* @__PURE__ */ jsxs6("span", { className: "photon-message-footer__cached", children: [
          /* @__PURE__ */ jsx7("span", { "aria-hidden": "true", children: "\xB7" }),
          /* @__PURE__ */ jsx7(Leaf, { size: 12 }),
          formatTokens(message.usage.tokens_cached)
        ] }) : null,
        message.usage?.cost_usd != null ? /* @__PURE__ */ jsxs6("span", { className: "photon-message-footer__cost", children: [
          "\xB7 $",
          message.usage.cost_usd.toFixed(4)
        ] }) : null,
        modelName ? /* @__PURE__ */ jsx7("span", { className: "photon-message-footer__model-name", children: modelName }) : null
      ] })
    ] })
  ] });
};
var MessageFooter_default = MessageFooter;

// src/index.ts
function useGenerationStatus(input = {}) {
  const { answerId } = input;
  const derived = deriveGenerationStatus(input);
  const monotonicRef = useRef4({
    answerId: void 0,
    status: GENERATION_STATUS.IDLE
  });
  if (monotonicRef.current.answerId !== answerId) {
    monotonicRef.current = { answerId, status: derived.status };
    return derived;
  }
  const previousStatus = monotonicRef.current.status;
  const statusRanks = STATUS_RANK;
  const previousRank = statusRanks[previousStatus] ?? 0;
  const nextRank = statusRanks[derived.status] ?? 0;
  if (nextRank < previousRank) {
    return { ...derived, status: previousStatus };
  }
  monotonicRef.current = { answerId, status: derived.status };
  return derived;
}
function useIsNewChat(chatId, user, chatInfo, isDataReady) {
  const [isNewChat, setIsNewChat] = useState5(false);
  const hasCreatedNewChatRef = useRef4(false);
  useEffect4(() => {
    if (!chatId || !user?.uid) return;
    if (chatInfo?.uid) {
      setIsNewChat(false);
      hasCreatedNewChatRef.current = false;
    } else if (isDataReady) {
      setIsNewChat(true);
    }
  }, [chatId, user?.uid, chatInfo, isDataReady]);
  const resetNewChatFlag = () => {
    hasCreatedNewChatRef.current = false;
  };
  return { isNewChat, resetNewChatFlag };
}
export {
  ChatComposerLayout,
  ChatNavigation,
  ChatNavigationLayout,
  ComposerControls,
  EditablePrompt,
  GenerationStatusIndicator_default as GenerationStatusIndicator,
  InlineSpinner_default as InlineSpinner,
  MessageFooter_default as MessageFooter,
  ResponseLoader_default as ResponseLoader,
  SparkIcon,
  SpectraIcon,
  useGenerationStatus,
  useIsNewChat
};
